import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NEW_OBJECT_ID } from 'utils/objectUtils';
import { TDateRangeRelations, TTEObject } from '@timeedit/types';
import {
  type RelationFilter,
  useLazyFindRelationsQuery,
} from '../api/relationsApi';

type RelationListInput = {
  /** The currently-edited working copy of the parent object. */
  object: TTEObject;
  /** Propagate the merged `relations` to Redux so existing Save/Discard plumbing handles persistence. */
  onChange: (patch: Partial<TTEObject>) => void;
};

type LoadPageArgs = {
  filter: RelationFilter | undefined;
  page: number;
};

type LoadPageResult = {
  hasMore: boolean;
  /**
   * Live totalCount as of right after the fetch — computed from refs so callers can return it
   * synchronously without waiting for React state to flush. Same value the table will eventually
   * read out of `totalCount`, just available one frame earlier.
   */
  totalCount: number;
  /**
   * The just-fetched page items. Returned so the caller can forward them as `results` to
   * `useInfiniteScroll` (which reads `count` and `data` from each fetch for internal paging
   * bookkeeping). Rendering still goes through `visibleItems`.
   */
  items: TTEObject[];
};

export type RelationListApi = {
  /** Items rendered in the list — pendingAdds first, then server items minus pendingRemoves. */
  visibleItems: TTEObject[];
  /** Server-reported gross total (pre-exclude) + pending diff. */
  totalCount: number;
  hasUnsavedChanges: boolean;
  isFetching: boolean;
  /** IDs of items the user marked for removal — drives `excludeIds` on subsequent fetches. */
  pendingRemoveIds: ReadonlySet<number>;
  /** IDs of pending-added items — drives the FieldChangedMark dirty highlight. */
  pendingAddIds: ReadonlySet<number>;
  /**
   * Single fetch entry point. Caller passes the filter and page directly so there's no captured
   * stale state — the hook never needs to "guess" what to fetch.
   */
  loadPage: (args: LoadPageArgs) => Promise<LoadPageResult>;
  /** Drop accumulated server items + reset gross total. Used as `resetFn` for the underlying table. */
  reset: () => void;
  /**
   * Re-selecting an already-removed object via the Add modal undoes the removal — global Discard
   * handles wholesale revert.
   */
  add: (objects: TTEObject[]) => void;
  remove: (objects: TTEObject[]) => void;
};

const buildAddedRelations = (objects: TTEObject[]): TDateRangeRelations[] =>
  objects.map((o) => ({ objectIds: [o.id], start: 0, end: 0 }));

export const useRelationList = ({
  object,
  onChange,
}: RelationListInput): RelationListApi => {
  const [serverItems, setServerItems] = useState<TTEObject[]>([]);
  const [grossTotal, setGrossTotal] = useState<number>(0);

  // Pending state lives outside Redux. The merged result is pushed to Redux via `onChange` so
  // the existing whole-object Save (PATCH /objects/:id) and resetObject Discard handle persistence.
  const [pendingAdds, setPendingAdds] = useState<TTEObject[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<Set<number>>(
    () => new Set()
  );

  // Latest values — read inside loadPage so the returned totalCount reflects current intent
  // without waiting for React's render to flush state updates. (loadPage also assigns
  // grossTotalRef on success so subsequent reads in the same tick stay consistent.)
  const pendingRemoveIdsRef = useRef(pendingRemoveIds);
  const pendingAddsRef = useRef(pendingAdds);
  const grossTotalRef = useRef(grossTotal);
  useEffect(() => {
    pendingRemoveIdsRef.current = pendingRemoveIds;
  }, [pendingRemoveIds]);
  useEffect(() => {
    pendingAddsRef.current = pendingAdds;
  }, [pendingAdds]);
  useEffect(() => {
    grossTotalRef.current = grossTotal;
  }, [grossTotal]);

  // Tracks the `relations` array we last pushed to Redux. When the next render's
  // `object.relations` is a different ref AND not the one we emitted, something else mutated
  // it — that's `resetObject` (Discard) on the form.
  const lastEmittedRelationsRef = useRef<TDateRangeRelations[]>(
    object.relations
  );

  const [trigger, lastResult] = useLazyFindRelationsQuery();

  const parentTypeIds = object.types;
  const parentObjectId = object.id;

  const reset = useCallback(() => {
    setServerItems([]);
    setGrossTotal(0);
    grossTotalRef.current = 0;
  }, []);

  // Reset pending + accumulator when the parent object changes.
  // (The drawer can switch between objects without unmounting this component.)
  useEffect(() => {
    setPendingAdds([]);
    setPendingRemoveIds(new Set());
    reset();
  }, [parentObjectId, reset]);

  // Detect Discard (resetObject) by watching `object.relations` change to a ref we didn't
  // emit. We can't rely on dirtyFields.relations: getDirtyFields only reports added items, so
  // a pure-removal session never marks it non-empty and the transition-to-empty signal we'd
  // want never fires. The relations-array reference, on the other hand, ALWAYS changes on
  // both add and remove paths.
  // Save isn't an issue here — it unmounts this component before the effect can fire.
  useEffect(() => {
    if (object.relations === lastEmittedRelationsRef.current) return;

    lastEmittedRelationsRef.current = object.relations;

    const hadPending =
      pendingAddsRef.current.length > 0 || pendingRemoveIdsRef.current.size > 0;
    if (!hadPending) return;

    // Pending state cleared + accumulator dropped. visibleItems goes to [], AdminTable triggers
    // a fresh page-1 fetch with empty excludeIds, restoring any items the server had hidden.
    setPendingAdds([]);
    setPendingRemoveIds(new Set());
    pendingAddsRef.current = [];
    pendingRemoveIdsRef.current = new Set();
    reset();
  }, [object.relations, reset]);

  const loadPage = useCallback<RelationListApi['loadPage']>(
    async ({ filter, page }) => {
      // Compute the current totalCount once — used both for the new-object short-circuit and
      // the error path so the header doesn't snap to 0 while rows stay rendered.
      const currentTotalCount =
        grossTotalRef.current +
        pendingAddsRef.current.length -
        pendingRemoveIdsRef.current.size;

      // Brand-new (unsaved) objects have id === NEW_OBJECT_ID (0). Issuing a relations fetch
      // for objectIds: [0] would be a wasted round-trip and could surface garbage results, so
      // short-circuit. Pending adds still render — they live in `pendingAdds` independently.
      if (parentObjectId == null || parentObjectId === NEW_OBJECT_ID) {
        return {
          hasMore: false,
          totalCount: currentTotalCount,
          items: [],
        };
      }
      try {
        const excludeIds = Array.from(pendingRemoveIdsRef.current);
        const result = await trigger({
          parentTypeIds,
          parentObjectId,
          page,
          excludeIds,
          filter,
        }).unwrap();

        setServerItems((prev) =>
          page === 1 ? result.data : [...prev, ...result.data]
        );
        // Server returns the post-exclude count. Adding back the count of IDs we asked it to
        // exclude recovers the stable "gross" relation count, so flipping excludeIds between
        // fetches doesn't move grossTotal up and down.
        const newGrossTotal = result.totalResults + excludeIds.length;
        setGrossTotal(newGrossTotal);
        grossTotalRef.current = newGrossTotal;

        // Caller may need the live totalCount before React flushes setGrossTotal.
        const totalCount =
          newGrossTotal +
          pendingAddsRef.current.length -
          pendingRemoveIdsRef.current.size;

        return {
          hasMore: result.nextCursor != null,
          totalCount,
          items: result.data,
        };
      } catch {
        // Transient error: keep already-rendered serverItems intact and report the unchanged
        // total so the table stays internally consistent. AdminTable sees hasMore=false so it
        // stops trying to load more for this attempt; the next user-driven fetch can retry.
        return {
          hasMore: false,
          totalCount: currentTotalCount,
          items: [],
        };
      }
    },
    [trigger, parentTypeIds, parentObjectId]
  );

  const pendingAddIds = useMemo(
    () => new Set(pendingAdds.map((o) => o.id)),
    [pendingAdds]
  );

  const visibleItems = useMemo(() => {
    const fromServer = serverItems.filter((o) => !pendingRemoveIds.has(o.id));
    return [...pendingAdds, ...fromServer];
  }, [pendingAdds, pendingRemoveIds, serverItems]);

  const totalCount = grossTotal + pendingAdds.length - pendingRemoveIds.size;

  const hasUnsavedChanges = pendingAdds.length > 0 || pendingRemoveIds.size > 0;

  const add = useCallback(
    (objects: TTEObject[]) => {
      if (objects.length === 0) return;

      // Re-selecting an already-removed object via the modal treats it as undo-the-removal.
      const toUnremove = objects.filter((o) => pendingRemoveIds.has(o.id));

      // Dedupe against the *full* current relation set, not just `serverItems`. With thousands
      // of relations only a couple pages are loaded; the rest live in object.relations only.
      const knownIds = new Set<number>([
        ...pendingAddIds,
        ...object.relations.flatMap((r) => r.objectIds),
      ]);
      const fresh = objects.filter(
        (o) => !pendingRemoveIds.has(o.id) && !knownIds.has(o.id)
      );

      if (toUnremove.length === 0 && fresh.length === 0) return;

      if (toUnremove.length > 0) {
        setPendingRemoveIds((prev) => {
          const next = new Set(prev);
          for (const o of toUnremove) next.delete(o.id);
          return next;
        });
      }
      if (fresh.length > 0) {
        setPendingAdds((prev) => [...prev, ...fresh]);
      }

      const restored = toUnremove.map((o) => ({
        objectIds: [o.id],
        start: 0,
        end: 0,
      }));
      const emitted = [
        ...object.relations,
        ...restored,
        ...buildAddedRelations(fresh),
      ];
      lastEmittedRelationsRef.current = emitted;
      onChange({ relations: emitted });
    },
    [pendingRemoveIds, pendingAddIds, object.relations, onChange]
  );

  const remove = useCallback(
    (objects: TTEObject[]) => {
      if (objects.length === 0) return;

      const ids = new Set(objects.map((o) => o.id));

      // Pending adds drop entirely — they never reached the server.
      const stillAdds = pendingAdds.filter((o) => !ids.has(o.id));
      if (stillAdds.length !== pendingAdds.length) {
        setPendingAdds(stillAdds);
      }

      // Server items go into pendingRemoveIds so future fetches send them via excludeIds.
      const idsThatWereServerItems = objects
        .filter((o) => !pendingAddIds.has(o.id))
        .map((o) => o.id);

      if (idsThatWereServerItems.length > 0) {
        setPendingRemoveIds((prev) => {
          const next = new Set(prev);
          for (const id of idsThatWereServerItems) next.add(id);
          return next;
        });
      }

      // Mirror the change into Redux's working copy so dirtyFields + Save reflect reality.
      const newRelations = object.relations
        .map((r) => ({
          ...r,
          objectIds: r.objectIds.filter((id) => !ids.has(id)),
        }))
        .filter((r) => r.objectIds.length > 0);
      lastEmittedRelationsRef.current = newRelations;
      onChange({ relations: newRelations });
    },
    [pendingAdds, pendingAddIds, object.relations, onChange]
  );

  return {
    visibleItems,
    totalCount,
    hasUnsavedChanges,
    isFetching: lastResult.isFetching,
    pendingRemoveIds,
    pendingAddIds,
    loadPage,
    reset,
    add,
    remove,
  };
};
